import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  TextInput, 
  toast,
  Select,
  DataTable,
  Switch
} from '@etherisc/ui-kit';
import { PositiveButton, NeutralButton } from '../../components/buttons';
import { useAuth } from '../../hooks/useAuth';
import { useRBAC, usePermissions } from '../../hooks/usePermissions';
import { useQuotes } from '../../hooks/useQuotes';
import { quoteService } from '../../services/quoteService';
import type { Quote } from '../../types/quote';
import type { ActionDefinition } from '../../utils/tableHelpers';
import { PermissionGate } from '../../components/PermissionGate';
import { 
  QUOTE_STATUS_LABELS,
  QUOTE_STATE_LABELS
} from '../../types/quote';
import {
  EditIcon,
  TrashIcon,
  SearchIcon,
  FileTextIcon,
  FilterIcon,
  EyeIcon,
} from 'lucide-react';

// Import column patterns for consistent table styling
import { 
  textColumn,
  dateColumn,
  statusColumn,
  currencyColumn,
  numberColumn,
} from '../../utils/columns';

export function QuoteManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isAdmin } = useRBAC();
  const { userPermissions } = usePermissions();
  const showSuccess = (message: string, description?: string) => toast.success(message, { description });
  const showError = (message: string, description?: string) => toast.error(message, { description });
  
  const {
    quotes,
    filteredQuotes,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    viewAllOrgs,
    setViewAllOrgs,
    pagination,
    statistics,
    statisticsLoading,
    refreshQuotes,
    fetchQuotes,
    updateQuoteOptimistically,
    deleteQuoteOptimistically,
  } = useQuotes();

  // Collection state management
  const [collectAllRunning, setCollectAllRunning] = useState(false);
  const [showCollectAllDialog, setShowCollectAllDialog] = useState(false);
  const [initiatePaymentsRunning, setInitiatePaymentsRunning] = useState(false);
  const [showInitiateDialog, setShowInitiateDialog] = useState(false);
  const [initiatePreviewData, setInitiatePreviewData] = useState<{
    totalQuotes: number;
    totalPremium: number;
    totalSumInsured: number;
    totalParticipants: number;
    quotesWithoutPaymentMethods: number;
    skippedQuotes?: Array<{
      id: string;
      quoteNumber: string;
      reason?: string;
    }>;
    canProceed: boolean;
  } | null>(null);

  // Handle optimistic updates from navigation state
  useEffect(() => {
    const state = location.state as { updatedQuote?: Quote } | null;
    if (state?.updatedQuote) {
      updateQuoteOptimistically(state.updatedQuote.id, state.updatedQuote);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, updateQuoteOptimistically, navigate, location.pathname]);

  // Handle search
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    fetchQuotes(1, filters, pagination.limit, viewAllOrgs);
  }, [filters, pagination.limit, viewAllOrgs, fetchQuotes, setSearchQuery]);

  // Handle status filter
  const handleStatusFilter = useCallback((status: string) => {
    const newFilters = {
      ...filters,
      status: status === 'all' ? undefined : status as 'TEMPLATE' | 'DRAFT' | 'PROPOSAL' | 'UNDER_REVIEW' | 'APPROVED' | 'EXECUTED' | 'REJECTED' | 'EXPIRED',
    };
    setFilters(newFilters);
    fetchQuotes(1, newFilters, pagination.limit, viewAllOrgs);
  }, [filters, setFilters, fetchQuotes, pagination.limit, viewAllOrgs]);

  // Handle product filter
  const handleProductFilter = useCallback((productId: string) => {
    const newFilters = {
      ...filters,
      productId: productId === 'all' ? undefined : productId,
    };
    setFilters(newFilters);
    fetchQuotes(1, newFilters, pagination.limit, viewAllOrgs);
  }, [filters, setFilters, fetchQuotes, pagination.limit, viewAllOrgs]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    const emptyFilters = {};
    setFilters(emptyFilters);
    setSearchQuery('');
    fetchQuotes(1, emptyFilters, pagination.limit, viewAllOrgs);
  }, [setFilters, setSearchQuery, fetchQuotes, pagination.limit, viewAllOrgs]);

  // Handle view all orgs toggle
  const handleViewAllOrgsToggle = useCallback((enabled: boolean) => {
    setViewAllOrgs(enabled);
    fetchQuotes(1, filters, pagination.limit, enabled);
  }, [setViewAllOrgs, fetchQuotes, filters, pagination.limit]);

  // Handle quote actions
  const handleCreate = useCallback(() => {
    navigate('/admin/quotes/create');
  }, [navigate]);

  const handleView = useCallback((quote: Quote) => {
    navigate(`/admin/quotes/${quote.id}`);
  }, [navigate]);

  const handleEdit = useCallback((quote: Quote) => {
    navigate(`/admin/quotes/${quote.id}/edit`);
  }, [navigate]);

  const handleDocuments = useCallback((quote: Quote) => {
    navigate(`/admin/quotes/${quote.id}/documents`);
  }, [navigate]);

  const handleDelete = useCallback(async (quote: Quote) => {
    if (!confirm(`Are you sure you want to delete quote "${quote.quoteNumber}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      deleteQuoteOptimistically(quote.id);
      showSuccess('Quote Deleted', `Quote "${quote.quoteNumber}" has been deleted successfully.`);
      await quoteService.deleteQuote(quote.id);
    } catch (error) {
      console.error('Error deleting quote:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete quote.';
      showError('Deletion Failed', errorMessage);
      refreshQuotes();
    }
  }, [deleteQuoteOptimistically, showSuccess, showError, refreshQuotes]);

  // Collection handlers
  const handleCollectionToggle = useCallback(async (quoteId: string, selected: boolean) => {
    try {
      const endpoint = selected 
        ? `/api/quotes/${quoteId}/prepare-for-payment-collection`
        : `/api/quotes/${quoteId}/deselect`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      showSuccess(result.message);
      
      // Refresh table to show state change
      await refreshQuotes();
      
    } catch (error) {
      console.error('Collection toggle error:', error);
      showError('Failed to update collection status');
    }
  }, [showSuccess, showError, refreshQuotes]);

  const handleCollectAll = useCallback(() => {
    setShowCollectAllDialog(true);
  }, []);

  const handleConfirmCollectAll = useCallback(async () => {
    setShowCollectAllDialog(false);
    try {
      const response = await fetch('/api/quotes/collect-all', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 409) {
        showError('Bulk collection is already running');
        setCollectAllRunning(true);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      setCollectAllRunning(true);
      showSuccess('Bulk collection started...');
      
    } catch (error) {
      console.error('Bulk collection failed:', error);
      showError('Failed to start bulk collection');
    }
  }, [showSuccess, showError]);

  // Real-time updates while bulk collection is running
  useEffect(() => {
    if (!collectAllRunning) return;
    
    const refreshInterval = setInterval(async () => {
      // Refresh the quotes table to show state changes
      await refreshQuotes();
    }, 1000); // Refresh every 1 second
    
    return () => clearInterval(refreshInterval);
  }, [collectAllRunning, refreshQuotes]);

  // Check collect-all status periodically
  useEffect(() => {
    if (!collectAllRunning) return;
    
    const statusInterval = setInterval(async () => {
      try {
        const response = await fetch('/api/quotes/collect-all/status', {
          credentials: 'include'
        });
        const result = await response.json();
        
        if (!result.running) {
          setCollectAllRunning(false);
          showSuccess('Bulk collection completed');
          // Final refresh to ensure all changes are visible
          await refreshQuotes();
        }
      } catch (error) {
        console.error('Status check failed:', error);
        // On error, stop the auto-refresh to prevent spam
        setCollectAllRunning(false);
      }
    }, 2000); // Check status every 2 seconds
    
    return () => clearInterval(statusInterval);
  }, [collectAllRunning, refreshQuotes, showSuccess]);

  // Initiate Payments handlers
  const handleInitiatePayments = useCallback(async () => {
    try {
      // Get preview data
      const response = await fetch('/api/quotes/initiate-payments/preview', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      setInitiatePreviewData(result.data);
      setShowInitiateDialog(true);
    } catch (error) {
      console.error('Failed to get payment initiation preview:', error);
      showError('Failed to load payment preview');
    }
  }, [showError]);

  const handleConfirmInitiatePayments = useCallback(async () => {
    try {
      const response = await fetch('/api/quotes/initiate-payments', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 409) {
        showError('Payment initiation is already running');
        setInitiatePaymentsRunning(true);
        setShowInitiateDialog(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      setInitiatePaymentsRunning(true);
      setShowInitiateDialog(false);
      showSuccess('Payment initiation started...');
      
    } catch (error) {
      console.error('Payment initiation failed:', error);
      showError('Failed to start payment initiation');
      setShowInitiateDialog(false);
    }
  }, [showSuccess, showError]);

  // Check initiate-payments status periodically
  useEffect(() => {
    if (!initiatePaymentsRunning) return;
    
    const statusInterval = setInterval(async () => {
      try {
        const response = await fetch('/api/quotes/initiate-payments/status', {
          credentials: 'include'
        });
        const result = await response.json();
        
        if (!result.data.running) {
          setInitiatePaymentsRunning(false);
          showSuccess('Payment initiation completed');
          // Final refresh to ensure all changes are visible
          await refreshQuotes();
        }
      } catch (error) {
        console.error('Payment initiation status check failed:', error);
        // On error, stop the auto-refresh to prevent spam
        setInitiatePaymentsRunning(false);
      }
    }, 2000); // Check every 2 seconds
    
    return () => clearInterval(statusInterval);
  }, [initiatePaymentsRunning, refreshQuotes, showSuccess]);

  // Define action definitions for the table
  const actionDefinitions: ActionDefinition<Quote>[] = [
    {
      id: 'view',
      label: 'View Quote',
      icon: <EyeIcon size={16} />,
      onClick: handleView,
      intent: 'outline',
    },
    {
      id: 'edit',
      label: 'Edit Quote',
      icon: <EditIcon size={16} />,
      onClick: handleEdit,
      intent: 'outline',
      hidden: (quote) => {
        if (!userPermissions?.includes('quotes:update')) return true;
        if (quote.state !== 'new') return true;
        if (isAdmin() || userPermissions?.includes('quotes:approve')) return false;
        return quote.organizationId !== user?.organization?.id;
      },
    },
    {
      id: 'documents',
      label: 'Quote documents',
      icon: <FileTextIcon size={16} />,
      onClick: handleDocuments,
      intent: 'outline',
      hidden: () => {
        // Check if user has permission to read quotes
        return !userPermissions?.includes('quotes:read');
      },
    },
    {
      id: 'delete',
      label: 'Delete Quote',
      icon: <TrashIcon size={16} />,
      onClick: handleDelete,
      intent: 'danger',
      hidden: (quote) => {
        // Check if user has permission first
        if (!userPermissions?.includes('quotes:delete')) {
          return true;
        }
        
        // Admin and Product Administrator users can delete all quotes
        if (isAdmin() || userPermissions?.includes('quotes:approve')) {
          return false;
        }
        
        // Regular users can only delete quotes from their own organization
        return quote.organizationId !== user?.organization?.id;
      },
      disabled: (quote) => ['activated'].includes(quote.state),
    },
  ];

  // Define columns using pattern-based helpers
  // Sequence: Quote#, Product, State, Policyholder, Sum Insured, Premium, # Participants, Created, "Collect" checkbox, Actions
  // Note: Financial columns use pre-calculated values from transformer for better performance
  const columns = [
    // 1. Quote Number
    textColumn<Quote>('quoteNumber', {
      header: 'Quote #',
      width: 140,
      className: 'font-mono text-sm'
    }),
    
    // 2. Product name
    textColumn<Quote>('product', {
      header: 'Product',
      getValue: (quote) => (quote as Quote).product?.name || 'Unknown Product',
      maxLength: 25
    }),
    
    // 3. State (replaces Status)
    statusColumn<Quote>('state', {
      header: 'State',
      transformLabel: (state) => QUOTE_STATE_LABELS[state as keyof typeof QUOTE_STATE_LABELS] || state
    }),
    
    // 4. Policyholder
    {
      id: 'policyholder',
      header: 'Policyholder',
      cell: ({ row }: { row: { original: Quote } }) => {
        const quote = row.original;
        const policyholder = quote.participants?.find((p) => p.role === 'POLICYHOLDER');
        if (policyholder?.farmer) {
          return policyholder.farmer.name;
        }
        if (policyholder?.organization) {
          return policyholder.organization.name;
        }
        return 'Not assigned';
      },
      sortable: true
    },
    
    // 5. Sum Insured (pre-calculated in transformer)
    currencyColumn<Quote>('displaySumInsured', {
      header: 'Sum Insured',
      emptyDisplay: 'Pending'
    }),
    
    // 6. Premium (pre-calculated in transformer)
    currencyColumn<Quote>('displayPremium', {
      header: 'Premium',
      emptyDisplay: 'Pending'
    }),
    
    // 7. # of Participants (pre-calculated in transformer)
    numberColumn<Quote>('participantCount', {
      header: '# Participants',
      width: 120
    }),
    
    // 8. Created date
    dateColumn<Quote>('createdAt', {
      header: 'Created',
      width: 120
    }),
    
    // 9. Collect Premium checkbox (only for approved quotes)
    {
      id: 'collectPremium',
      header: 'Collect',
      cell: ({ row }: { row: { original: Quote } }) => {
        const quote = row.original;
        
        // Only show checkbox for approved quotes (either approved or ready_for_payment_collection state)
        const quoteState = quote.state;
        if (quoteState !== 'approved' && quoteState !== 'ready_for_payment_collection') {
          return <span className="text-gray-400 text-sm">-</span>;
        }
        
        const isReadyForCollection = quoteState === 'ready_for_payment_collection';
        
        return (
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={isReadyForCollection}
              onChange={(e) => handleCollectionToggle(quote.id, e.target.checked)}
              disabled={loading || collectAllRunning || initiatePaymentsRunning}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            {collectAllRunning && (quote.state as string) === 'approved' && (
              <div className="ml-2 w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            )}
            {initiatePaymentsRunning && (quote.state as string) === 'ready_for_payment_collection' && (
              <div className="ml-2 w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
        );
      },
      width: 80
    },
    
    // 10. Actions column
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      enableResizing: false,
      size: 140,
      cell: ({ row }: { row: { original: Quote } }) => {
        const quote = row.original;
        
        // Filter visible actions
        const visibleActions = actionDefinitions.filter(action => !action.hidden?.(quote));
        
        return (
          <div className="flex items-center gap-2">
            {visibleActions.map((action) => (
              <button
                key={action.id}
                onClick={() => action.onClick(quote)}
                disabled={action.disabled?.(quote)}
                className={`p-1 rounded transition-colors ${
                  action.intent === 'danger' 
                    ? action.disabled?.(quote) 
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-red-600 hover:text-red-800'
                    : action.disabled?.(quote)
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-blue-600 hover:text-blue-800'
                }`}
                title={action.label}
              >
                {action.icon}
              </button>
            ))}
          </div>
        );
      },
    },
  ];

  // Filter options
  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    ...Object.entries(QUOTE_STATUS_LABELS).map(([value, label]) => ({ value, label })),
  ];

  const productOptions = [
    { value: 'all', label: 'All Products' },
    ...Array.from(new Set(quotes.map(q => q.product?.name).filter(Boolean)))
      .map(name => ({ value: name!, label: name! })),
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quote Management</h1>
          <p className="text-gray-600 mt-1">
            Manage insurance quotes, proposals, and coverage requests
            {collectAllRunning && (
              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-1 block"></span>
                Collecting premiums...
              </span>
            )}
            {initiatePaymentsRunning && (
              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                <span className="w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin mr-1 block"></span>
                Initiating payments...
              </span>
            )}
          </p>
        </div>
        
        <div className="flex gap-2">
          {/* Collect All Button */}
          <PermissionGate permission="quotes:update">
            <NeutralButton 
              onClick={handleCollectAll}
              disabled={collectAllRunning}
              className={collectAllRunning ? "opacity-50" : ""}
            >
              {collectAllRunning ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                  Collecting... 
                </>
              ) : (
                'Collect All Approved'
              )}
            </NeutralButton>
          </PermissionGate>
          
          {/* Initiate Collection Button */}
          <PermissionGate permission="quotes:update">
            <PositiveButton 
              onClick={handleInitiatePayments}
              disabled={initiatePaymentsRunning || collectAllRunning || (statistics?.readyForPaymentQuotes === 0)}
              className={initiatePaymentsRunning ? "opacity-50" : ""}
            >
              {initiatePaymentsRunning ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                  Initiating... 
                </>
              ) : (
                'Initiate Collection'
              )}
            </PositiveButton>
          </PermissionGate>
          
          <PermissionGate permission="quotes:create">
            <PositiveButton onClick={handleCreate}>
              <FileTextIcon className="w-4 h-4" />
              Create Quote
            </PositiveButton>
          </PermissionGate>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-primary">
            {statisticsLoading || !statistics ? '...' : statistics.totalQuotes}
          </div>
          <div className="text-sm text-base-content/70">
            Total Quotes {viewAllOrgs ? '(All Organisations)' : '(Your Organisation)'}
          </div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600">
            {statisticsLoading || !statistics ? '...' : statistics.draftQuotes}
          </div>
          <div className="text-sm text-base-content/70">Draft Quotes</div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {statisticsLoading || !statistics ? '...' : statistics.approvedQuotes}
          </div>
          <div className="text-sm text-base-content/70">Approved</div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">
            {statisticsLoading || !statistics ? '...' : statistics.readyForPaymentQuotes}
          </div>
          <div className="text-sm text-base-content/70">Ready for Collection</div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600">
            {statisticsLoading || !statistics ? '...' : statistics.paymentInitiatedQuotes}
          </div>
          <div className="text-sm text-base-content/70">Collection Initiated</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <FilterIcon className="w-5 h-5" />
            Search and Filters
          </h3>
          <div className="flex items-center gap-4">
            {isAdmin() && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={viewAllOrgs}
                  onCheckedChange={handleViewAllOrgsToggle}
                  id="view-all-orgs"
                />
                <label htmlFor="view-all-orgs" className="text-sm font-medium text-gray-700 cursor-pointer">
                  View quotes from all organisations
                </label>
                {viewAllOrgs && (
                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    Admin Mode: All Organisations
                  </span>
                )}
              </div>
            )}
            {(searchQuery || filters.status || filters.productId) && (
              <NeutralButton onClick={handleClearFilters} className="text-sm">
                Clear All
              </NeutralButton>
            )}
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-base-content/50 z-10" />
          <div className="pl-10">
            <TextInput
              placeholder="Search quotes by number, product, participant name..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Filter Row */}
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Status"
            options={statusOptions}
            value={filters.status || 'all'}
            onValueChange={(value: string) => handleStatusFilter(value)}
            placeholder="All Statuses"
          />
          <Select
            label="Product"
            options={productOptions}
            value={filters.productId || 'all'}
            onValueChange={(value: string) => handleProductFilter(value)}
            placeholder="All Products"
          />
        </div>

        
        {/* Active Filters Display */}
        {(searchQuery || filters.status || filters.productId) && (
          <div className="text-sm text-gray-600">
            Active filters: {[
              searchQuery && `Search: "${searchQuery}"`,
              filters.status && `Status: ${QUOTE_STATUS_LABELS[filters.status as keyof typeof QUOTE_STATUS_LABELS]}`,
              filters.productId && `Product: ${filters.productId}`
            ].filter(Boolean).join(', ')}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Quotes</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <NeutralButton onClick={refreshQuotes}>
                  Try Again
                </NeutralButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quotes Table */}
      <DataTable
        data={filteredQuotes}
        columns={columns}
        enableSorting={true}
        enableResizing={true}
        loading={loading}
        manualPagination={true}
        pageCount={pagination.totalPages}
        rowCount={pagination.total}
        onPaginationChange={(paginationState) => {
          const newPage = paginationState.pageIndex + 1; // Convert 0-based to 1-based
          const newPageSize = paginationState.pageSize;
          
          // Let the DataTable handle pagination state, just fetch data
          fetchQuotes(newPage, filters, newPageSize, viewAllOrgs);
        }}
        state={{
          pagination: {
            pageIndex: pagination.page - 1, // Convert 1-based to 0-based
            pageSize: pagination.limit,
          },
        }}
        pagination={{
          showSizeSelector: true,
          showPageInfo: true,
          showNavigation: true,
          enableFastNavigation: true,
          enableJumpToPage: true,
          pageSizeOptions: [10, 25, 50, 100],
        }}
      />

      {/* Collect All Confirmation Dialog */}
      {showCollectAllDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Collect All Approved</h3>
            
            <div className="space-y-3 mb-6">
              <p className="text-gray-600">
                This will mark all approved quotes as ready for payment collection.
              </p>
              <div className="flex justify-between">
                <span className="text-gray-600">Approved quotes to collect:</span>
                <span className="font-semibold">{statistics?.approvedQuotes ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Estimated total premium:</span>
                <span className="font-semibold">
                  ${quotes
                    .filter(q => q.state === 'approved')
                    .reduce((sum, q) => sum + (Number(q.displayPremium) || Number(q.premiumAmount) || 0), 0)
                    .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <NeutralButton onClick={() => setShowCollectAllDialog(false)}>
                Cancel
              </NeutralButton>
              <PositiveButton onClick={handleConfirmCollectAll}>
                Confirm
              </PositiveButton>
            </div>
          </div>
        </div>
      )}

      {/* Payment Initiation Confirmation Dialog */}
      {showInitiateDialog && initiatePreviewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Initiate Payment Processing</h3>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-600">Quotes ready for payment:</span>
                <span className="font-semibold">{initiatePreviewData.totalQuotes}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total participants:</span>
                <span className="font-semibold">{initiatePreviewData.totalParticipants}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total premium to collect:</span>
                <span className="font-semibold">${initiatePreviewData.totalPremium.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total sum insured:</span>
                <span className="font-semibold">${initiatePreviewData.totalSumInsured.toLocaleString()}</span>
              </div>
              
              {initiatePreviewData.quotesWithoutPaymentMethods > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-yellow-800 text-sm font-semibold mb-2">
                    ⚠️ {initiatePreviewData.quotesWithoutPaymentMethods} quote(s) will be skipped due to missing payment methods:
                  </p>
                  {initiatePreviewData.skippedQuotes && initiatePreviewData.skippedQuotes.length > 0 && (
                    <ul className="text-yellow-800 text-sm list-disc list-inside space-y-1">
                      {initiatePreviewData.skippedQuotes.map((quote) => (
                        <li key={quote.id}>
                          Quote {quote.quoteNumber}
                          {quote.reason && <span className="text-yellow-600"> - {quote.reason}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex gap-3 justify-end">
              <NeutralButton 
                onClick={() => setShowInitiateDialog(false)}
                disabled={initiatePaymentsRunning}
              >
                Cancel
              </NeutralButton>
              <PositiveButton 
                onClick={handleConfirmInitiatePayments}
                disabled={!initiatePreviewData.canProceed || initiatePaymentsRunning}
              >
                {initiatePaymentsRunning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </>
                ) : (
                  'Confirm & Initiate'
                )}
              </PositiveButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 